import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Calendar as CalendarIcon, DollarSign, Users, MapPin, CheckCircle, AlertCircle, MessageCircle } from "lucide-react";
import { buildWhatsAppUrl } from "@/utils/whatsapp";
import { checkProductAvailability } from "@/lib/availabilityChecker";
import { format } from "date-fns";
import { getWeatherForecast } from "@/functions/getWeatherForecast";
import { createCheckout } from "@/functions/createCheckout";
import WeatherCard from "@/components/WeatherCard";

export default function ProductDetail() {
  const { productId } = useParams();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(null);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    delivery_address: "",
    city: "",
    state: "Texas",
    zip_code: "",
  });

  const { data: product, isLoading: productLoading } = useQuery({
    queryKey: ["product", productId],
    queryFn: () => base44.entities.Product.get(productId),
    enabled: !!productId,
  });

  const { data: addOns = [] } = useQuery({
    queryKey: ["addons"],
    queryFn: () => base44.entities.AddOn.filter({ active_status: true }),
  });

  useEffect(() => {
    if (selectedDate && productId) {
      setCheckingAvailability(true);
      checkProductAvailability(productId, selectedDate.toISOString().split("T")[0])
        .then((result) => setAvailabilityStatus(result))
        .catch(console.error)
        .finally(() => setCheckingAvailability(false));
    }
  }, [selectedDate, productId]);

  const fetchWeather = async (location, date) => {
    if (!location || !date) return;
    setWeatherLoading(true);
    try {
      const res = await getWeatherForecast({ location, event_date: date });
      setWeather(res.data);
    } catch {
      setWeather({ status: "unavailable", weather_message: "Weather forecast is temporarily unavailable.", weather_checked: true });
    } finally {
      setWeatherLoading(false);
    }
  };

  // Re-fetch weather whenever date or city/zip changes (debounced to avoid
  // burning through the free open-meteo API quota on every keystroke)
  useEffect(() => {
    const location = customerInfo.zip_code || customerInfo.city;
    const date = selectedDate ? selectedDate.toISOString().split("T")[0] : null;
    if (!location || !date || location.trim().length < 3) {
      setWeather(null);
      return;
    }
    const timer = setTimeout(() => {
      fetchWeather(location.trim(), date);
    }, 600);
    return () => clearTimeout(timer);
  }, [selectedDate, customerInfo.zip_code, customerInfo.city]);

  const toggleAddOn = (addonId) => {
    setSelectedAddOns((prev) =>
      prev.includes(addonId) ? prev.filter((id) => id !== addonId) : [...prev, addonId]
    );
  };

  const rentalPrice = product?.price || 0;
  const addOnsTotal = addOns
    .filter((addon) => selectedAddOns.includes(addon.id))
    .reduce((sum, addon) => sum + addon.price, 0);
  const totalPrice = rentalPrice + addOnsTotal;
  const reservationFee = product?.reservation_fee ?? 40;
  const balanceDue = totalPrice - reservationFee;

  const createReservationMutation = useMutation({
    mutationFn: async (data) => {
      // Save customer for marketing list (skip if email already exists)
      const existing = await base44.entities.Customer.filter({ email: customerInfo.email });
      let customer = existing?.[0] || null;
      if (!customer) {
        customer = await base44.entities.Customer.create({
          first_name: customerInfo.first_name,
          last_name: customerInfo.last_name,
          email: customerInfo.email,
          phone: customerInfo.phone,
          city: customerInfo.city,
          state: customerInfo.state,
          zip_code: customerInfo.zip_code,
        }).catch(() => null);
      }

      const reservation = await base44.entities.Reservation.create({
        ...data,
        customer_id: customer?.id || "",
      });
      return reservation;
    },
    onSuccess: async (reservation) => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      // Check for iframe (checkout only works from published app)
      if (window.self !== window.top) {
        alert("Checkout is only available from the published app, not inside the editor preview.");
        return;
      }
      setCheckoutLoading(true);
      const origin = window.location.origin;
      const res = await createCheckout({
        reservationId: reservation.id,
        reservationNumber: reservation.reservation_number,
        productName: product?.name || "",
        reservationFee: reservationFee,
        totalPrice: totalPrice,
        customerEmail: customerInfo.email,
        customerName: `${customerInfo.first_name} ${customerInfo.last_name}`,
        successUrl: `${origin}/payment-success?reservation_id=${reservation.id}`,
        cancelUrl: `${origin}/product/${productId}`,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        alert("Could not start checkout: " + (res.data?.error || "Unknown error"));
        setCheckoutLoading(false);
      }
    },
    onError: (error) => {
      alert("Something went wrong: " + error.message);
    },
  });

  const validate = () => {
    const errors = {};
    if (!selectedDate) errors.date = "Please select an event date.";
    if (!availabilityStatus?.available) errors.date = "Selected date is not available.";
    if (!customerInfo.first_name.trim()) errors.first_name = "Required";
    if (!customerInfo.last_name.trim()) errors.last_name = "Required";
    if (!customerInfo.email.trim()) errors.email = "Required";
    if (!customerInfo.phone.trim()) errors.phone = "Required";
    if (!customerInfo.delivery_address.trim()) errors.delivery_address = "Required";
    if (!customerInfo.city.trim()) errors.city = "Required";
    if (!customerInfo.zip_code.trim()) errors.zip_code = "Required";
    return errors;
  };

  const handleSubmit = () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Scroll to first error
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setFormErrors({});

    const reservationData = {
      reservation_number: `RES-${Date.now()}`,
      customer_id: "",
      product_id: productId,
      event_date: selectedDate.toISOString().split("T")[0],
      start_time: "10:00",
      end_time: "18:00",
      delivery_address: `${customerInfo.delivery_address}, ${customerInfo.city}, ${customerInfo.state} ${customerInfo.zip_code}`,
      total_price: totalPrice,
      reservation_fee_paid: reservationFee,
      balance_due: balanceDue,
      payment_status: "Pending",
      reservation_status: "Pending Payment",
      notes: `Customer: ${customerInfo.first_name} ${customerInfo.last_name}, Email: ${customerInfo.email}, Phone: ${customerInfo.phone}, City: ${customerInfo.city}, State: ${customerInfo.state}, Zip: ${customerInfo.zip_code}`,
      weather_checked: weather?.weather_checked || false,
      weather_forecast_date: weather?.weather_forecast_date || null,
      weather_high_temp: weather?.weather_high_temp || null,
      weather_rain_chance: weather?.weather_rain_chance || null,
      weather_message: weather?.weather_message || null,
      weather_status: weather?.status || null,
    };

    createReservationMutation.mutate(reservationData);
  };

  if (productLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-muted border-t-foreground rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Product Not Found</h1>
          <Link to="/WaterSlides"><Button>Browse Products</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">


      <div className="max-w-6xl mx-auto px-6 py-12">
        <Link to="/WaterSlides" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div>
            <img
              src={product.image_urls?.[0] || "https://via.placeholder.com/600x400"}
              alt={product.name}
              className="w-full h-auto rounded-2xl shadow-lg"
            />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-foreground mb-4">{product.name}</h1>
            {product.short_description && (
              <p className="text-lg font-medium text-foreground mb-3">{product.short_description}</p>
            )}
            {product.description && (
              <p className="text-base text-muted-foreground mb-6">{product.description}</p>
            )}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-primary" />
                <span className="text-2xl font-bold text-primary">${product.price}/day</span>
              </div>
              {product.dimensions && (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Users className="w-5 h-5" />
                  <span>Dimensions: {product.dimensions}</span>
                </div>
              )}
              {product.max_riders && (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Users className="w-5 h-5" />
                  <span>Max Riders: {product.max_riders}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {formErrors.date && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {formErrors.date}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  Select Event Date
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="flex-shrink-0">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      className="rounded-md border"
                      disabled={(date) => date < new Date()}
                    />
                    {selectedDate && (
                      <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${
                        checkingAvailability
                          ? "bg-muted text-muted-foreground"
                          : availabilityStatus?.available
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-red-50 text-red-700 border border-red-200"
                      }`}>
                        {checkingAvailability ? (
                          <span className="text-sm">Checking availability...</span>
                        ) : availabilityStatus?.available ? (
                          <><CheckCircle className="w-4 h-4" /><span className="text-sm font-medium">Available for this date.</span></>
                        ) : (
                          <><AlertCircle className="w-4 h-4" /><span className="text-sm font-medium">Unavailable. Please select another date.</span></>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 w-full md:pt-1">
                    <div className="mb-3">
                      <label className="text-sm font-medium mb-1 block">Your ZIP Code or City</label>
                      <Input
                        placeholder="e.g. 77001 or Houston"
                        value={customerInfo.zip_code || customerInfo.city}
                        onChange={(e) => {
                          const val = e.target.value;
                          // If it looks like a ZIP (digits), put in zip_code; otherwise city
                          if (/^\d+$/.test(val)) {
                            setCustomerInfo({ ...customerInfo, zip_code: val, city: "" });
                          } else {
                            setCustomerInfo({ ...customerInfo, city: val, zip_code: "" });
                          }
                        }}
                        className="max-w-xs"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {!selectedDate ? "Select a date to see the weather forecast." : ""}
                      </p>
                    </div>
                    <WeatherCard
                      weather={weather}
                      eventDate={selectedDate ? selectedDate.toISOString().split("T")[0] : null}
                      loading={weatherLoading}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Delivery Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">First Name {formErrors.first_name && <span className="text-red-500">*</span>}</label>
                    <Input value={customerInfo.first_name} onChange={(e) => setCustomerInfo({...customerInfo, first_name: e.target.value})} className={formErrors.first_name ? "border-red-400" : ""} />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Last Name {formErrors.last_name && <span className="text-red-500">*</span>}</label>
                    <Input value={customerInfo.last_name} onChange={(e) => setCustomerInfo({...customerInfo, last_name: e.target.value})} className={formErrors.last_name ? "border-red-400" : ""} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Email {formErrors.email && <span className="text-red-500">*</span>}</label>
                    <Input type="email" value={customerInfo.email} onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})} className={formErrors.email ? "border-red-400" : ""} />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Phone {formErrors.phone && <span className="text-red-500">*</span>}</label>
                    <Input value={customerInfo.phone} onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})} className={formErrors.phone ? "border-red-400" : ""} />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Delivery Address {formErrors.delivery_address && <span className="text-red-500">*</span>}</label>
                  <Input value={customerInfo.delivery_address} onChange={(e) => setCustomerInfo({...customerInfo, delivery_address: e.target.value})} placeholder="Street address" className={formErrors.delivery_address ? "border-red-400" : ""} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">City {formErrors.city && <span className="text-red-500">*</span>}</label>
                    <Input value={customerInfo.city} onChange={(e) => setCustomerInfo({...customerInfo, city: e.target.value})} className={formErrors.city ? "border-red-400" : ""} />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">State</label>
                    <Input value={customerInfo.state} onChange={(e) => setCustomerInfo({...customerInfo, state: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">ZIP Code {formErrors.zip_code && <span className="text-red-500">*</span>}</label>
                    <Input value={customerInfo.zip_code} onChange={(e) => setCustomerInfo({...customerInfo, zip_code: e.target.value})} className={formErrors.zip_code ? "border-red-400" : ""} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Optional Add-Ons</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {addOns.length === 0 && <p className="text-muted-foreground text-sm">No add-ons available.</p>}
                {addOns.map((addon) => (
                  <div key={addon.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={selectedAddOns.includes(addon.id)} onCheckedChange={() => toggleAddOn(addon.id)} />
                      <div>
                        <p className="font-medium">{addon.name}</p>
                        <p className="text-sm text-muted-foreground">{addon.description}</p>
                      </div>
                    </div>
                    <span className="font-bold text-primary">+${addon.price}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Pricing Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Rental Price</span>
                    <span className="font-medium">${rentalPrice}</span>
                  </div>
                  {addOnsTotal > 0 && (
                    <div className="flex justify-between">
                      <span>Add-Ons ({selectedAddOns.length})</span>
                      <span className="font-medium">${addOnsTotal}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span>${totalPrice}</span>
                  </div>
                </div>

                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Reservation Fee (due today)</span>
                    <span className="font-medium">${reservationFee}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Remaining Balance</span>
                    <span className="font-bold text-primary">${balanceDue}</span>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  A ${reservationFee} reservation fee secures your date. Remaining balance due before delivery.
                </p>

                <a
                  href={buildWhatsAppUrl(product?.name, selectedDate ? format(selectedDate, "MMMM d, yyyy") : null)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full"
                >
                  <button className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold text-sm rounded-md px-4 py-2.5 transition-all duration-200 hover:scale-[1.02] shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="w-4 h-4 fill-white flex-shrink-0">
                      <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.738 5.474 2.027 7.785L0 32l8.418-2.006A15.93 15.93 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 0 1-6.769-1.847l-.485-.289-5.001 1.192 1.27-4.862-.316-.499A13.267 13.267 0 0 1 2.667 16C2.667 8.637 8.637 2.667 16 2.667S29.333 8.637 29.333 16 23.363 29.333 16 29.333zm7.293-9.907c-.4-.2-2.366-1.167-2.733-1.3-.367-.133-.634-.2-.9.2-.267.4-1.033 1.3-1.267 1.567-.233.267-.467.3-.867.1-.4-.2-1.687-.622-3.213-1.98-1.187-1.058-1.989-2.367-2.222-2.767-.233-.4-.025-.616.175-.816.18-.18.4-.467.6-.7.2-.233.267-.4.4-.667.133-.267.067-.5-.033-.7-.1-.2-.9-2.167-1.233-2.967-.325-.78-.656-.674-.9-.686l-.767-.013c-.267 0-.7.1-1.067.5s-1.4 1.367-1.4 3.333 1.433 3.867 1.633 4.133c.2.267 2.82 4.307 6.833 6.04.955.413 1.7.66 2.28.844.958.305 1.83.262 2.52.159.769-.115 2.366-.967 2.7-1.9.333-.933.333-1.733.233-1.9-.1-.167-.367-.267-.767-.467z" />
                    </svg>
                    Ask About This Rental
                  </button>
                </a>

                <Button
                  type="button"
                  className="w-full"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={createReservationMutation.isPending || checkoutLoading}
                >
                  {(createReservationMutation.isPending || checkoutLoading) ? (
                    <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {checkoutLoading ? "Redirecting to Payment..." : "Processing..."}</span>
                  ) : (
                    `Pay Reservation Fee — $${reservationFee}`
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}